import {
  Button,
  DateRangePicker,
  type DateRangePickerProps,
  Dialog,
  Icon,
  IconSizeClasses,
  Select,
  type SelectOption,
} from '@eppendorf/vnls-react-components';
import { SubscriptionTypeNames } from '@eppendorf/vnls-subscription-utils';
import cn from 'classnames';
import { endOfDay, format, startOfDay, subMonths } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useTokenAttributes } from '$shared/custom-hooks/useTokenAttributes';

import { SubscriptionBanner } from '$components/subscription-banner';
import { useDeviceSnapshot } from '$features/monitoring/device-snapshot.api';
import { useEmailMonitoringExportWithQueryParams } from '$features/monitoring/export/monitoring-export.api';
import { useDeviceTelemetryDataForSnapshot } from '$features/monitoring/parameters/useDeviceTelemetryDataForSnapshot';
import {
  isFeatureLimited,
  useGetSubscription,
} from '$features/subscription/subscription-api';

import styles from './email-monitoring-export.module.scss';

const getLastThreeMonths = (today: Date) => {
  const months = [];
  for (let i = 0; i < 3; i += 1) {
    const date = subMonths(today, i);

    // Create UTC dates for the first and last day of the month
    const year = date.getFullYear();
    const month = date.getMonth();

    const firstDay = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
    const lastDay = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));

    months.push({
      name: format(date, 'MMMM'),
      year: date.getFullYear(),
      start: firstDay.toISOString(),
      end: lastDay.toISOString(),
    });
  }
  return months;
};

const getLastDays = (today: Date, daysBefore: number) => {
  // Calculate the start date by going back exactly daysBefore days
  // Use UTC to avoid timezone conversion issues
  const todayDateString = today.toISOString().split('T')[0];
  const todayUTC = new Date(`${todayDateString}T00:00:00.000Z`);
  const startDayUTC = new Date(todayUTC.getTime() - daysBefore * 24 * 60 * 60 * 1000);

  return [startDayUTC.toISOString(), today.toISOString()];
};

export function EmailMonitoringExport() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { email, profile } = useTokenAttributes(['email', 'profile']);
  const isSupportUser = profile === 'SupportUser';

  const { data: subscription, isPending: isSubscriptionPending } = useGetSubscription();

  const exportLimited = isFeatureLimited(subscription);

  const options = useMemo(() => {
    const today = new Date();

    // Match backend validation logic: retention days - 1 is the max days back allowed
    // Essentials: 8 retention days - 1 = 7 days back allowed
    // SampleSafetyPlus: 91 retention days - 1 = 90 days back allowed
    const maxDaysForSubscription =
      subscription?.subscriptionType === SubscriptionTypeNames.Essentials ? 7 : 90;

    const allOptions = [
      {
        label: t('monitoringDetail.emailExportDetail.last7Days'),
        // For Essentials: exactly 7 days back to match backend validation
        value: getLastDays(today, 7).join(','),
      },
    ];

    // Only add monthly options if subscription allows
    if (maxDaysForSubscription >= 30) {
      allOptions.push(
        ...getLastThreeMonths(today).map((month) => ({
          label: `${month.name} ${month.year}`,
          value: `${month.start},${month.end}`,
        })),
      );
    }

    // Only add 90-day option if subscription allows
    if (maxDaysForSubscription >= 90) {
      allOptions.push({
        label: t('monitoringDetail.emailExportDetail.lastXDays', { days: 90 }),
        value: getLastDays(today, 90).join(','),
      });
    }

    return allOptions;
  }, [subscription?.subscriptionType, t]);

  const [selectedOption, setSelectedOption] = useState<SelectOption>(options[0]);
  const [supportUserDateRange, setSupportUserDateRange] =
    useState<DateRangePickerProps['dateRange']>();
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  // Update selected option when options change (e.g., when subscription loads)
  useEffect(() => {
    if (
      options.length > 0 &&
      (!selectedOption || !options.find((opt) => opt.value === selectedOption.value))
    ) {
      setSelectedOption(options[0]);
    }
  }, [options, selectedOption]);

  const deviceManufacturer = searchParams.get('manufacturer')!;
  const deviceSerialNumber = searchParams.get('serialNumber')!;

  const { data: snapshot, isLoading: isLoadingSnapshot } = useDeviceSnapshot({
    manufacturer: deviceManufacturer,
    serialNumber: deviceSerialNumber,
  });

  const { primaryParameters } = useDeviceTelemetryDataForSnapshot(snapshot);

  const getBaseParams = () => {
    const ianaTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return {
      serialNumber: snapshot?.inventoryDevice.serialNumber || '',
      parameters: primaryParameters.map((param) => ({
        name: param.name ?? param.telemetryId,
        subject: param.telemetryId,
      })),
      ianaTimezone,
    };
  };

  const getParamsFromSelectedOption = () => {
    const timeStamps = selectedOption?.value?.split(',');
    const start = timeStamps?.[0] ?? '';
    const end = timeStamps?.[1] ?? '';
    const timespan = selectedOption?.label ?? '';

    return {
      ...getBaseParams(),
      startDate: start,
      endDate: end,
      exportPeriodName: timespan,
    };
  };

  const getParamsFromCustomDateRange = () => {
    const rangeStart = supportUserDateRange?.from;
    const rangeEnd = supportUserDateRange?.to ?? supportUserDateRange?.from;

    const start = rangeStart ? startOfDay(rangeStart).toISOString() : '';
    const end = rangeEnd ? endOfDay(rangeEnd).toISOString() : '';
    const timespan =
      rangeStart && rangeEnd
        ? `${format(rangeStart, 'yyyy-MM-dd')} - ${format(rangeEnd, 'yyyy-MM-dd')}`
        : '';

    return {
      ...getBaseParams(),
      startDate: start,
      endDate: end,
      exportPeriodName: timespan,
    };
  };

  const { isError, isPending, mutate, isSuccess } =
    useEmailMonitoringExportWithQueryParams();

  const startDownloadForSelectedOption = async () => {
    // Re-compute params with fresh data at mutation time
    const params = getParamsFromSelectedOption();
    mutate(params);
  };

  const startDownloadForCustomDateRange = async () => {
    // Re-compute params with fresh data at mutation time
    const params = getParamsFromCustomDateRange();
    mutate(params);
  };

  useEffect(() => {
    if (!isPending && isSuccess) {
      setOpenConfirmationDialog(true);
    }
  }, [isPending, isSuccess]);

  return (
    <div className="flex flex__dir--column flex__ai--center w-full h-full text-align-center">
      <span className="m-bottom-l">
        <Icon name="csv" className="bg-blue-500" size={IconSizeClasses.Large} />
      </span>

      <div className="font-size-xl font-weight-medium m-bottom-m">
        <Trans i18nKey="monitoringDetail.emailExportDetail.title" />
      </div>
      <div className="m-bottom-m">
        <Trans
          i18nKey="monitoringDetail.emailExportDetail.hint"
          components={{ br: <br /> }}
        />
      </div>
      <div className="m-bottom-xl">
        <Trans
          i18nKey="monitoringDetail.emailExportDetail.note"
          components={{ bold: <span className="font-weight-bold" /> }}
        />
      </div>

      {!isSubscriptionPending && exportLimited && (
        <SubscriptionBanner
          iconName="calendar"
          headerI18nKey="monitoringDetail.emailExportDetail.limitSubscriptionBannerHeader"
          messageI18nKey="monitoringDetail.emailExportDetail.limitSubscriptionBannerText"
        />
      )}

      <div className={`flex__dir--row flex__ai--center gap-l ${cn(styles.exportRanges)}`}>
        {!isSubscriptionPending && !exportLimited && (
          <Select
            defaultValue={options[0].value}
            options={options}
            onChange={(option) =>
              setSelectedOption(
                options.find((x) => x.value === option) ?? ({} as SelectOption),
              )
            }
          />
        )}
        <Button
          onClick={startDownloadForSelectedOption}
          disabled={isPending || isSubscriptionPending || isLoadingSnapshot}
        >
          <Trans i18nKey="monitoringDetail.emailExportDetail.buttonText" />
        </Button>
      </div>

      {isSupportUser && (
        <>
          <div className="font-size-m font-weight-medium m-top-xl m-bottom-m">
            <Trans i18nKey="monitoringDetail.emailExportDetail.customRangeTitle" />
          </div>
          <div
            className={`flex__dir--row flex__ai--center gap-l ${cn(styles.exportRanges)}`}
          >
            <DateRangePicker
              dateRange={supportUserDateRange}
              onDateRangeChange={setSupportUserDateRange}
              disabled={{ after: new Date() }}
            />
            <Button
              onClick={startDownloadForCustomDateRange}
              disabled={
                isPending ||
                isSubscriptionPending ||
                isLoadingSnapshot ||
                !supportUserDateRange?.from
              }
            >
              <Trans i18nKey="monitoringDetail.emailExportDetail.customRangeButtonText" />
            </Button>
          </div>
        </>
      )}

      {isError && (
        <div className="font-size-xl font-weight-medium color-red-600">
          <Trans i18nKey="monitoringDetail.emailExportDetail.errorMessage" />
        </div>
      )}

      <Dialog
        isOpen={openConfirmationDialog}
        onOpenChange={(state) => setOpenConfirmationDialog(state)}
        hideCloseButton
        className="email-monitoring-export"
      >
        <div className="flex flex__dir--column flex__ai--center">
          <Icon
            name="info"
            size={IconSizeClasses.XLarge}
            className="bg-blue-500 m-bottom-xxl"
          />
          <Dialog.Title>
            <Trans i18nKey="monitoringDetail.emailExportDetail.successMessage" />
          </Dialog.Title>
          <Dialog.Description>
            <Trans
              i18nKey="monitoringDetail.emailExportDetail.successMessageHint"
              components={{ bold: <span className="font-weight-bold" /> }}
              values={{ email }}
            />
          </Dialog.Description>
        </div>
        <Dialog.Actions>
          <Button onClick={() => setOpenConfirmationDialog(false)} className="btn--solid">
            {t('shared.close')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </div>
  );
}
